// src/lib/gigRouting.js
// Routing helpers for the React version of Performer Pocket Planner.
//
// ── EXPORTS ───────────────────────────────────────────────────
//
//   calculateGigTimes(date, showTime, prepMinutes, venueAddress,
//                     getReadyLocation, prepAddress, callTime)
//     Main export used by AddGig on form submit.
//     Calculates leaveTime, makeupStartTime, leaveHomeTime
//     by routing from home → (prep location →) venue via OSRM.
//
//   calculateVenueToVenueLeaveTime(fromVenueAddress, toVenueAddress, toCallTime)
//     Used by TodaysGigCard when the performer has back-to-back
//     gigs on the same day and wants to drive directly from one venue
//     to the next without going home first.
//     Returns { leaveTime, driveMinutes } or null on failure.
//
// ── LOCATION CONCEPTS ────────────────────────────────────────
//
//   HOME ADDRESS (notification_preferences)
//     The fixed address saved in Settings → Home Location.
//     Used by home-mode and venue-mode routing as the journey origin.
//
//   CURRENT GPS LOCATION (navigator.geolocation)
//     Where the user is RIGHT NOW.
//     FIX: used as the origin for the home→prep leg in 'other' modes
//     because the performer may be in a different city when saving
//     the gig (e.g. on tour, at a hotel, at a friend's place).
//     Falls back to the saved home address if GPS is unavailable.
//
// ── get_ready_location values ────────────────────────────────
//
//   home        Full prep at home. Route: home → venue.
//   home_half   50% prep at home, finish 50% at venue.
//   home_most   70% prep at home, 30% at venue.
//   other       Full prep at a different address (hotel, studio).
//               Route: CURRENT LOCATION → prep location → venue.
//   other_half  50% at prep location, 50% at venue.
//   other_most  70% at prep location, 30% at venue.
//   venue       Drive first, do full prep on arrival.
//               Leave time = callTime - drive - prepMinutes.

import { supabase } from './supabaseClient'

// ── Constants ─────────────────────────────────────────────────
const HOME_SETTLING_BUFFER_MIN = 15   // breathing room after arriving before starting face
const VENUE_BUFFER_MIN         = 20   // park + find dressing room + settle in

const NOMINATIM_UA = 'DragPocketPlanner/1.0 (https://drag-helper.netlify.app)'


// ── getCurrentPosition ────────────────────────────────────────
//
// Wraps navigator.geolocation.getCurrentPosition in a Promise so it
// can be awaited. Returns { lat, lng } or null.
//
// FIX: used in 'other' mode to route from the performer's actual
// current position to the prep location, instead of their saved
// home address. This handles the common case where the performer
// is already traveling (hotel, friend's city) when they add the gig.
//
// Timeout is intentionally short (5 s) so a GPS denial or lack of
// signal does not freeze the gig-save flow. If it times out or is
// denied, the caller falls back to the saved home address.
function getCurrentPosition(timeoutMs = 5000) {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      // browser does not support geolocation -- fall back silently
      console.warn('[gigRouting] navigator.geolocation not available; using home address.')
      resolve(null)
      return
    }

    const timer = setTimeout(() => {
      // GPS did not respond in time -- fall back silently
      console.warn('[gigRouting] getCurrentPosition timed out; using home address.')
      resolve(null)
    }, timeoutMs)

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        // success: we have a fresh GPS fix
        clearTimeout(timer)
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        })
        console.log('[gigRouting] Using current GPS location for home→prep leg.')
      },
      (err) => {
        // denied or unavailable -- fall back silently
        clearTimeout(timer)
        console.warn('[gigRouting] GPS unavailable:', err.message, '-- using home address.')
        resolve(null)
      },
      {
        enableHighAccuracy: false,
        // false = faster fix; we only need city-level accuracy for drive-time estimates
        timeout: timeoutMs,
        maximumAge: 5 * 60 * 1000,
        // accept a cached position up to 5 minutes old; saves battery
      },
    )
  })
}


// ── geocodeAddress ────────────────────────────────────────────
//
// Geocodes a plain-text address string to { lat, lng } via Nominatim.
// Returns null on any failure so callers can degrade gracefully.
async function geocodeAddress(address) {
  if (!address || !address.trim()) return null
  try {
    const url     = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(address)}&format=json&limit=1`
    const res     = await fetch(url, { headers: { 'User-Agent': NOMINATIM_UA } })
    const results = await res.json()
    if (!results || results.length === 0) {
      console.warn('[gigRouting] Nominatim found no results for:', address)
      return null
    }
    return { lat: parseFloat(results[0].lat), lng: parseFloat(results[0].lon) }
  } catch (err) {
    console.warn('[gigRouting] Geocode error:', err.message)
    return null
  }
}


// ── getDrivingDuration ────────────────────────────────────────
//
// Fetches driving duration in ceiling minutes between two lat/lng
// pairs using the OSRM public demo server.
// Rounds up so the performer always has a small buffer.
async function getDrivingDuration(fromLat, fromLng, toLat, toLng) {
  try {
    // OSRM expects coordinates as lng,lat (GeoJSON convention)
    const url  = `https://router.project-osrm.org/route/v1/driving/${fromLng},${fromLat};${toLng},${toLat}?overview=false`
    const res  = await fetch(url)
    const data = await res.json()
    if (!data.routes || data.routes.length === 0) {
      console.warn('[gigRouting] OSRM returned no route.')
      return null
    }
    return Math.ceil(data.routes[0].duration / 60)
  } catch (err) {
    console.warn('[gigRouting] OSRM error:', err.message)
    return null
  }
}


// ── subtractMinutes ───────────────────────────────────────────
//
// Subtracts minutes from an "HH:MM" string, wrapping at midnight.
function subtractMinutes(timeStr, minutes) {
  const [h, m]  = timeStr.split(':').map(Number)
  const total   = h * 60 + m - minutes
  const wrapped = ((total % 1440) + 1440) % 1440
  const hh      = Math.floor(wrapped / 60)
  const mm      = wrapped % 60
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`
}


// ════════════════════════════════════════════════════════════════
// calculateVenueToVenueLeaveTime
//
// Used by TodaysGigCard for back-to-back gigs on the same day.
// Returns { leaveTime, driveMinutes } or null on failure.
// ════════════════════════════════════════════════════════════════
export async function calculateVenueToVenueLeaveTime(fromVenueAddress, toVenueAddress, toCallTime) {

  const [fromCoords, toCoords] = await Promise.all([
    geocodeAddress(fromVenueAddress),
    geocodeAddress(toVenueAddress),
  ])

  if (!fromCoords) {
    console.warn('[gigRouting] v2v: could not geocode departure venue:', fromVenueAddress)
    return null
  }
  if (!toCoords) {
    console.warn('[gigRouting] v2v: could not geocode destination venue:', toVenueAddress)
    return null
  }

  const driveMinutes = await getDrivingDuration(
    fromCoords.lat, fromCoords.lng,
    toCoords.lat,   toCoords.lng,
  )

  if (driveMinutes === null) {
    console.warn('[gigRouting] v2v: could not calculate drive time.')
    return null
  }

  const leaveTime = subtractMinutes(toCallTime, driveMinutes + VENUE_BUFFER_MIN)

  console.log(`[gigRouting] v2v: leave at ${leaveTime} | ${driveMinutes}min drive | ${VENUE_BUFFER_MIN}min buffer`)
  return { leaveTime, driveMinutes }
}


// ════════════════════════════════════════════════════════════════
// calculateGigTimes — main export used by AddGig
//
// Parameters:
//   date              "YYYY-MM-DD"
//   showTime          "HH:MM"
//   prepMinutes       total prep time in minutes
//   venueAddress      full venue address for geocoding
//   getReadyLocation  compound ready location string
//   prepAddress       getting-ready address ('other*' modes only)
//   callTime          "HH:MM" user-set call time (falls back to showTime - 60min)
//
// Returns: { leaveTime, makeupStartTime, leaveHomeTime } or null
// ════════════════════════════════════════════════════════════════
export async function calculateGigTimes(
  date,
  showTime,
  prepMinutes,
  venueAddress,
  getReadyLocation,
  prepAddress = null,
  callTime    = null,
) {

  // ── 1. Load saved home coords from notification_preferences ─
  // Used for home-mode, venue-mode, and as a fallback for other-mode
  // when GPS is unavailable.
  const { data: { user } } = await supabase.auth.getUser()
  let homeLat = null, homeLng = null

  if (user) {
    const { data: prefs } = await supabase
      .from('notification_preferences')
      .select('home_lat, home_lng')
      .eq('user_id', user.id)
      .single()

    if (prefs?.home_lat && prefs?.home_lng) {
      homeLat = prefs.home_lat
      homeLng = prefs.home_lng
    }
  }

  // ── 2. Geocode the venue ─────────────────────────────────────
  const venueCoords = await geocodeAddress(venueAddress)
  if (!venueCoords) {
    console.warn('[gigRouting] Could not geocode venue, skipping routing.')
    return null
  }

  // ── 3. Effective call time ────────────────────────────────────
  const effectiveCallTime = (callTime && callTime.trim())
    ? callTime.trim().slice(0, 5)
    : subtractMinutes(showTime, VENUE_BUFFER_MIN + 40)

  console.log(`[gigRouting] Call time: ${effectiveCallTime} (${callTime ? 'user-set' : 'auto'})`)

  // ── 4. Prep-at-location fraction ─────────────────────────────
  const isOther = getReadyLocation.startsWith('other')
  const isHome  = getReadyLocation.startsWith('home')
  const isVenue = getReadyLocation === 'venue'

  let prepAtLocationA = prepMinutes
  if (getReadyLocation.endsWith('_half')) prepAtLocationA = Math.round(prepMinutes * 0.5)
  if (getReadyLocation.endsWith('_most')) prepAtLocationA = Math.round(prepMinutes * 0.7)


  try {

    // ── VENUE mode ────────────────────────────────────────────
    // Drive home → venue first, do all prep on arrival.
    if (isVenue) {
      if (!homeLat) return null
      const driveMin = await getDrivingDuration(homeLat, homeLng, venueCoords.lat, venueCoords.lng)
      if (driveMin === null) return null
      const leaveTime = subtractMinutes(effectiveCallTime, driveMin + prepMinutes + VENUE_BUFFER_MIN)
      console.log(`[gigRouting] venue mode: leave ${leaveTime} | ${driveMin}min drive`)
      return { leaveTime, makeupStartTime: null, leaveHomeTime: null }
    }


    // ── HOME modes ────────────────────────────────────────────
    // Prep at the saved home address, then drive to venue.
    if (isHome) {
      if (!homeLat) return null
      const driveToVenueMin = await getDrivingDuration(homeLat, homeLng, venueCoords.lat, venueCoords.lng)
      if (driveToVenueMin === null) return null
      const leaveTime       = subtractMinutes(effectiveCallTime, driveToVenueMin + VENUE_BUFFER_MIN)
      const makeupStartTime = subtractMinutes(leaveTime, prepAtLocationA + HOME_SETTLING_BUFFER_MIN)
      console.log(`[gigRouting] home mode: leave ${leaveTime} | makeup ${makeupStartTime}`)
      return { leaveTime, makeupStartTime, leaveHomeTime: null }
    }


    // ── OTHER modes ───────────────────────────────────────────
    // Route: CURRENT LOCATION → prep location → venue.
    //
    // FIX: the home→prep leg now starts from the performer's current
    // GPS position rather than the saved home address. This is the
    // correct behaviour because the performer may be anywhere when
    // they save the gig -- a different city, a hotel, a tour stop.
    //
    // GPS is requested with a 5-second timeout. If it is denied,
    // unavailable, or too slow, getCurrentPosition returns null and
    // we fall back to the saved home address (same behaviour as before
    // the fix, so nothing regresses for users who decline location).
    if (isOther) {
      if (!prepAddress) {
        console.warn('[gigRouting] other mode: no prep address provided.')
        return null
      }

      const prepCoords = await geocodeAddress(prepAddress)
      if (!prepCoords) {
        console.warn('[gigRouting] other mode: could not geocode prep address:', prepAddress)
        return null
      }

      // Try to get the performer's current GPS position.
      // If GPS fails, fall back to the saved home address.
      const gpsCoords    = await getCurrentPosition()
      const originLat    = gpsCoords?.lat ?? homeLat
      const originLng    = gpsCoords?.lng ?? homeLng
      const originLabel  = gpsCoords ? 'GPS' : 'saved home'
      // used only for the console.log below so we know which was used

      // Run prep→venue and (if we have an origin) origin→prep in parallel.
      const [driveOriginToPrep, drivePrepToVenue] = await Promise.all([
        (originLat && originLng)
          ? getDrivingDuration(originLat, originLng, prepCoords.lat, prepCoords.lng)
          : Promise.resolve(null),
        getDrivingDuration(prepCoords.lat, prepCoords.lng, venueCoords.lat, venueCoords.lng),
      ])

      if (drivePrepToVenue === null) return null

      const leaveTime       = subtractMinutes(effectiveCallTime, drivePrepToVenue + VENUE_BUFFER_MIN)
      const makeupStartTime = subtractMinutes(leaveTime, prepAtLocationA + HOME_SETTLING_BUFFER_MIN)

      // leaveHomeTime is the time to leave the origin (current location
      // or saved home) to arrive at the prep location with a buffer.
      let leaveHomeTime = null
      if (driveOriginToPrep !== null) {
        leaveHomeTime = subtractMinutes(makeupStartTime, driveOriginToPrep + HOME_SETTLING_BUFFER_MIN)
      }

      console.log(
        `[gigRouting] other mode (origin: ${originLabel}):`,
        `leave origin ${leaveHomeTime} |`,
        `makeup ${makeupStartTime} |`,
        `leave for venue ${leaveTime}`,
      )

      return { leaveTime, makeupStartTime, leaveHomeTime }
    }


    return null

  } catch (err) {
    console.error('[gigRouting] Error:', err)
    return null
  }
}